/// <reference types="vite/client" />
import type { Order, OrderItem, ItemStatus } from './types';

// In production, call the Worker directly. In dev, use Vite proxy.
const API_BASE = (import.meta as any).env?.PROD
  ? 'https://name-frames.link-a-charm.workers.dev/api'
  : '/api';

/**
 * Extract CF Access JWT from cookies
 * CF Access stores the JWT in the CF_Authorization cookie after login
 */
function getCFAccessToken(): string | null {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.trim().split('=');
    if (name === 'CF_Authorization') {
      return valueParts.join('='); // Handle = in JWT
    }
  }
  return null;
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getCFAccessToken();

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include', // Include cookies for cross-origin requests
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Cf-Access-Jwt-Assertion': token } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));

    // If unauthorized, user may need to re-authenticate via CF Access
    if (response.status === 401) {
      console.error('Authentication required. Please ensure you are logged in via Cloudflare Access.');
    }

    // Some admin endpoints attach an upstream `detail` (e.g. Amazon's error body)
    const base = error.error || `HTTP ${response.status}`;
    throw new Error(error.detail ? `${base} (${error.detail})` : base);
  }

  return response.json();
}

// Orders
export async function listOrders(options?: {
  status?: string;
  view?: 'active' | 'all';
  completedDays?: number;
}): Promise<{
  orders: Order[];
  filter: { view: string; status: string | null; completedDays: number | null };
}> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.view) params.set('view', options.view);
  if (options?.completedDays) params.set('completedDays', String(options.completedDays));
  const queryString = params.toString();
  return request(`/orders${queryString ? `?${queryString}` : ''}`);
}

export async function getOrder(id: string): Promise<{ order: Order }> {
  return request(`/orders/${id}`);
}

export async function createOrder(data: {
  buyerName: string;
  amazonOrderId?: string;
  items: Array<{
    name: string;
    pronouns: string;
    frameMaterial: 'cherry' | 'sapele';
    needsProof: boolean;
    culturalContext?: string;
    notes?: string;
    poemThemes?: string;
    amazonOrderItemId?: string;
  }>;
}): Promise<{ order: Order }> {
  return request('/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOrder(
  id: string,
  data: {
    buyerName?: string;
    status?: string;
    items?: Array<{
      name?: string;
      pronouns?: string;
      culturalContext?: string;
      notes?: string;
      poemThemes?: string;
      status?: ItemStatus;
    }>;
  }
): Promise<{ order: Order }> {
  return request(`/orders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteOrder(id: string): Promise<{ success: boolean }> {
  return request(`/orders/${id}`, { method: 'DELETE' });
}

// Item management
export async function addItem(
  orderId: string,
  data: {
    name: string;
    pronouns: string;
    culturalContext?: string;
    notes?: string;
    poemThemes?: string;
  }
): Promise<{ order: Order }> {
  return request(`/orders/${orderId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateItem(
  orderId: string,
  itemIndex: number,
  data: Partial<OrderItem>
): Promise<{ order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteItem(
  orderId: string,
  itemIndex: number
): Promise<{ order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}`, { method: 'DELETE' });
}

// Origin generation (item-indexed)
export async function generateOrigin(
  orderId: string,
  itemIndex: number
): Promise<{ origin: string; order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}/origin/generate`, { method: 'POST' });
}

export async function refineOrigin(
  orderId: string,
  itemIndex: number,
  feedback: string
): Promise<{ origin: string; order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}/origin/refine`, {
    method: 'POST',
    body: JSON.stringify({ feedback }),
  });
}

export async function editOriginLine(
  orderId: string,
  itemIndex: number,
  lineIndex: number,
  newText: string
): Promise<{ origin: string; order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}/origin/edit-line`, {
    method: 'POST',
    body: JSON.stringify({ lineIndex, newText }),
  });
}

export async function approveOrigin(
  orderId: string,
  itemIndex: number
): Promise<{ order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}/origin/approve`, { method: 'POST' });
}

// Poem generation (item-indexed)
export async function generatePoem(
  orderId: string,
  itemIndex: number
): Promise<{ poem: string; order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}/poem/generate`, { method: 'POST' });
}

export async function refinePoem(
  orderId: string,
  itemIndex: number,
  feedback: string
): Promise<{ poem: string; order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}/poem/refine`, {
    method: 'POST',
    body: JSON.stringify({ feedback }),
  });
}

export async function editPoemLine(
  orderId: string,
  itemIndex: number,
  lineIndex: number,
  newText: string
): Promise<{ poem: string; order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}/poem/edit-line`, {
    method: 'POST',
    body: JSON.stringify({ lineIndex, newText }),
  });
}

export async function approvePoem(
  orderId: string,
  itemIndex: number
): Promise<{ order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}/poem/approve`, { method: 'POST' });
}

// File generation
export async function generateFiles(
  orderId: string
): Promise<{
  success: boolean;
  files: Array<{ itemIndex: number; name: string; pdfUrl: string; svgUrl: string }>;
  order: Order;
}> {
  return request(`/orders/${orderId}/generate-files`, { method: 'POST' });
}

export async function generateItemFiles(
  orderId: string,
  itemIndex: number
): Promise<{ success: boolean; pdfUrl: string; svgUrl: string; order: Order }> {
  return request(`/orders/${orderId}/items/${itemIndex}/generate-files`, { method: 'POST' });
}

// Order proof generation
export async function pullAmazonCustomization(
  orderId: string
): Promise<{ success: boolean; itemsWithData: number; filled: Record<string, string[]>; order: Order }> {
  return request(`/orders/${orderId}/amazon-customization`, { method: 'POST' });
}

export async function getProofMessage(): Promise<{ text: string; source: 'kv' | 'default' }> {
  return request('/templates/proof-message');
}

export async function generateOrderProof(
  orderId: string
): Promise<{ success: boolean; proofPdfUrl: string; order: Order }> {
  return request(`/orders/${orderId}/generate-proof`, { method: 'POST' });
}

